import { $config, $waitForClient } from '@client/core/atoms/config.js';
import { setGamTargetingAtom } from '@client/core/atoms/gamTargeting.js';
import {
  getPlacementList,
  updatePlacementStatuses
} from '@client/core/atoms/placements.js';
import {
  refreshKeywords,
  type FilterUpdatePayloadType
} from '@client/core/utils/refreshKeywords.js';
import type { Event } from '@podium/browser';
import { events, messageBus } from '@schibsted-nmp/advertising-events';
import {
  debug,
  DisplayPlacementId,
  PageType
} from '@schibsted-nmp/advertising-shared';

type TravelPageLoadedEvent = Event<{
  pageType: string;
  page?: number;

  destination?: {
    continent?: string;
    country?: string;
    city?: string;
  };

  destinasjon?: {
    continent?: string;
    country?: string;
    city?: string;
  };

  subvertical: string;
}>;

export function setupTravelEvents() {
  const { pageType } = $config.get() ?? {};

  if (pageType === PageType.Front) {
    messageBus.subscribe<{ subvertical: string }>(
      'travel',
      'subvertical',
      (event) => {
        debug.message('Subvertical event received', event.payload).verbose();

        const placements = getPlacementList();

        setGamTargetingAtom([
          {
            key: 'subvertical',
            value: [event.payload.subvertical]
          }
        ]);

        const travelPlacements: DisplayPlacementId[] = [
          DisplayPlacementId.AdvtMiddle1,
          DisplayPlacementId.AdvtMiddle2
        ];

        const placementsToRefresh = placements.filter((placement) =>
          travelPlacements.includes(placement.placementId as DisplayPlacementId)
        );

        updatePlacementStatuses(placementsToRefresh, 'refresh');
      }
    );
  }

  if (pageType === PageType.Result) {
    messageBus.subscribe<FilterUpdatePayloadType>(
      events.SEARCH.channel,
      events.SEARCH.FILTER.topic,
      (event) => {
        refreshKeywords({
          ...event.payload,
          payload: { searchParams: event.payload }
        }).catch((error: unknown) => {
          debug.message('Failed refreshing keywords', error).error();
        });
      }
    );
  }

  try {
    // Used by the travel vertical to set keywords on page load because they're not getting all the proper pageOpts
    const peekEvent: TravelPageLoadedEvent | undefined = messageBus.peek(
      'page',
      'loaded'
    );

    debug.message('Peeked page.loaded event', peekEvent).verbose();

    if (peekEvent) {
      travelLoadedCallback(peekEvent);
    } else {
      messageBus.subscribe('page', 'loaded', (event: TravelPageLoadedEvent) => {
        travelLoadedCallback(event);
      });
    }
  } catch (error) {
    debug
      .message('Failed to peek page.loaded event, subscribing instead', error)
      .warn();
  }
}

function travelLoadedCallback(event: TravelPageLoadedEvent) {
  $waitForClient.set(true);
  debug.message('Travel page loaded event received', event).verbose();

  refreshKeywords({
    ...event.payload,
    payload: { searchParams: event.payload }
  })
    .then(() => {
      $waitForClient.set(false);
    })
    .catch((error: unknown) => {
      debug.message('Failed refreshing keywords', error).error();
    });
}
